import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useFavorites } from "@/hooks/useFavorites";
import { useSEO } from "@/hooks/useSEO";
import { categoryList, videoList } from "@/mocks/videos";

export default function Detail() {
  const { id = "" } = useParams();
  const video = videoList.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();

  useSEO({
    title: video ? `${video.title}在线播放 - 桔子视频` : "视频未找到 - 桔子视频",
    description: video
      ? `${video.description.slice(0, 60)}。${video.typeName} · ${video.genres.join(" ")}，${video.year} 年 ${video.region} 出品。`
      : "该视频不存在或已下架。",
    keywords: video
      ? `${video.title},${video.typeName},${video.genres.join(",")},在线播放,桔子视频`
      : "桔子视频",
  });

  const [playing, setPlaying] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setShareOpen(false);
    setCopied(false);
  }, [id]);

  const related = useMemo(() => {
    if (!video) return [];
    return videoList
      .filter((item) => item.typeKey === video.typeKey && item.id !== video.id)
      .slice(0, 8);
  }, [video]);

  if (!video) {
    return (
      <Layout>
        <div className="mx-auto max-w-[1280px] px-4 py-24 text-center md:px-6">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-error-warning-line" />
          </span>
          <h1 className="mt-4 text-xl font-semibold text-foreground-900">视频未找到</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的影视内容不存在或已下架。</p>
          <Link
            to="/"
            className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const category = categoryList.find((item) => item.key === video.typeKey);
  const favorite = isFavorite(video.id);
  const shareUrl = `${window.location.origin}/detail/${video.id}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // clipboard may be unavailable; the link is still shown for manual copy
    }
    setCopied(true);
  };

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: video.typeName, to: `/category/${video.typeKey}` },
            { label: video.title },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
              <img
                src={video.cover}
                alt={`${video.title} 播放画面`}
                className={`h-full w-full object-cover object-top transition-opacity ${
                  playing ? "opacity-60" : "opacity-90"
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/30 to-transparent" />

              <button
                type="button"
                onClick={() => setPlaying((prev) => !prev)}
                aria-label={playing ? "暂停" : "播放"}
                className="absolute inset-0 flex cursor-pointer items-center justify-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/95 text-2xl text-background-50 transition-transform hover:scale-105">
                  <i className={playing ? "ri-pause-fill" : "ri-play-fill"} />
                </span>
              </button>

              <div className="pointer-events-none absolute inset-x-0 bottom-0 px-4 pb-4">
                <div className="h-1 w-full overflow-hidden rounded-full bg-background-50/30">
                  <div
                    className={`h-full rounded-full bg-primary-500 transition-all duration-1000 ${
                      playing ? "w-1/3" : "w-0"
                    }`}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-background-100">
                  <span className="flex items-center gap-1.5">
                    <i className={playing ? "ri-equalizer-line" : "ri-pause-circle-line"} />
                    {playing ? "正在播放（演示）" : "点击播放（演示播放器）"}
                  </span>
                  <span>{video.duration}</span>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2 text-xs text-primary-700">
              <i className="ri-information-line text-base" />
              本播放区为界面演示，暂不提供真实视频流；播放/暂停按钮仅作交互示意。
            </div>

            <section className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-lg font-bold text-foreground-950 font-heading">剧情简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{video.description}</p>

              <div className="mt-5 flex flex-wrap gap-2">
                {video.genres.map((g) => (
                  <span
                    key={g}
                    className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-700"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-foreground-900">所属分类</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Link
                    to={`/category/${video.typeKey}`}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    <span className="flex h-5 w-5 items-center justify-center">
                      <i className={category ? category.icon : "ri-film-line"} />
                    </span>
                    {video.typeName}
                  </Link>
                </div>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h1 className="text-xl font-bold text-foreground-950 font-heading">{video.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <span className="flex items-center gap-1 font-semibold text-primary-600">
                  <i className="ri-star-fill" />
                  {video.rating.toFixed(1)}
                </span>
                <span>{video.year}</span>
                <span>{video.region}</span>
                <span>{video.duration}</span>
              </div>

              <dl className="mt-5 space-y-3 border-t border-background-200 pt-5 text-sm">
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                  <dd className="text-foreground-900">{video.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-foreground-900">{video.actors.join(" / ")}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">类型</dt>
                  <dd className="text-foreground-900">
                    {video.typeName} · {video.genres.join(" ")}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">首播</dt>
                  <dd className="text-foreground-900">{video.year} 年</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">地区</dt>
                  <dd className="text-foreground-900">{video.region}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => toggleFavorite(video.id)}
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                    favorite
                      ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                      : "border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-600"
                  }`}
                >
                  <i className={favorite ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} />
                  {favorite ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCopied(false);
                    setShareOpen(true);
                  }}
                  className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-700"
                >
                  <i className="ri-share-forward-line text-base" />
                  分享
                </button>
              </div>
              <p className="mt-3 text-xs text-foreground-400">收藏保存在本地浏览器，仅作功能演示。</p>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl font-heading">相关推荐</h2>
            <p className="mt-1 text-sm text-foreground-500">同类别的其他优质内容，或许你也会喜欢</p>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {related.map((item) => (
                <VideoCard key={item.id} video={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      {shareOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-background-50 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground-950">分享给好友</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setShareOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <p className="mt-2 text-sm text-foreground-500">
              复制下面的链接，即可把《{video.title}》分享给你的朋友。
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                <i className="ri-link" />
              </span>
              <input
                readOnly
                value={shareUrl}
                className="min-w-0 flex-1 bg-transparent text-sm text-foreground-800 outline-none"
              />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100"
              >
                关闭
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
                {copied ? "已复制" : "复制链接"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}